import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, getDownloadURL, listAll, uploadBytes } from 'firebase/storage';
import { FIREBASE_STORAGE, FIREBASE_DB } from './firebase.config';
import { useAuth } from './context/AuthContext';
import { useTranslation } from 'react-i18next';
import { FileText, Download, Eye, Upload, AlertCircle } from 'lucide-react';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';

const DocumentsPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!currentUser) {
        setError('Please log in to access documents');
        setLoading(false);
        return;
      }

      try {
        // List all files in the Onboarding folder
        const onboardingRef = ref(FIREBASE_STORAGE, 'Onboarding');
        const result = await listAll(onboardingRef);
        
        // Get document metadata and URLs
        const docs = await Promise.all(
          result.items.map(async (item) => {
            try {
              const url = await getDownloadURL(item);
              return {
                id: item.name,
                title: item.name.replace(/_/g, ' ').replace('.pdf', ''),
                fileName: item.name,
                downloadUrl: url,
                error: null
              };
            } catch (error) {
              console.error(`Error fetching ${item.name}:`, error);
              return {
                id: item.name,
                title: item.name.replace(/_/g, ' ').replace('.pdf', ''),
                fileName: item.name,
                downloadUrl: null,
                error: 'Unable to load document'
              };
            }
          })
        );

        // Filter out SBG Employee Manual as requested
        const filteredDocs = docs.filter(doc => {
          const title = doc.title.toUpperCase();
          return !title.includes('SBG EMPLOYEE MANUAL');
        });

        setDocuments(filteredDocs);
        await fetchUploadedDocuments();
        setError(null);
      } catch (error) {
        console.error('Error fetching documents:', error);
        setError('Error loading documents. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [currentUser]);

  const fetchUploadedDocuments = async () => {
    try {
      const uploadsQuery = query(
        collection(FIREBASE_DB, 'uploadedDocuments'),
        where('employeeId', '==', currentUser.uid),
        where('documentType', '==', 'onboarding')
      );
      
      const querySnapshot = await getDocs(uploadsQuery);
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ id: doc.id, ...doc.data() });
      });
      setUploadedDocs(docs);
    } catch (error) {
      console.error('Error fetching uploaded documents:', error);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      // Check file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        alert('Only PDF, DOC, or DOCX files are allowed');
        return;
      }

      setUploading(true);
      
      // Create a reference to the file in Firebase Storage
      const storageRef = ref(FIREBASE_STORAGE, `userDocuments/${currentUser.uid}/${file.name}`);
      
      // Upload the file
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Save the document metadata to Firestore
      await addDoc(collection(FIREBASE_DB, 'uploadedDocuments'), {
        employeeId: currentUser.uid,
        fileName: file.name,
        fileUrl: downloadURL,
        uploadDate: new Date().toISOString(),
        status: 'pending_review',
        fileType: file.type,
        fileSize: file.size,
        documentType: 'onboarding'
      });

      await fetchUploadedDocuments();
      setUploading(false);
      alert('Document uploaded successfully!');
    } catch (error) {
      console.error('Error uploading document:', error);
      setUploading(false);
      alert('Error uploading document. Please try again.');
    }
  };

  const handleDownload = async (document) => {
    if (!document.downloadUrl) {
      try {
        const url = await getDownloadURL(ref(FIREBASE_STORAGE, `Onboarding/${document.fileName}`));
        window.open(url, '_blank');
      } catch (error) {
        console.error('Error downloading document:', error);
        setError('Error downloading document. Please try again.');
      }
    } else {
      window.open(document.downloadUrl, '_blank');
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Please log in to access employee documents.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-green-800 mb-4">
            {t('documents.title')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('documents.subtitle')}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-8 border-l-4 border-orange-500">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start">
              <div className="bg-orange-100 p-2 rounded-full mr-3">
                <FileText className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-green-800">
                  Employee Manual Acknowledgment
                </h2>
                <p className="mt-2 text-gray-600">
                  Review and sign the employee manual through Adobe Sign. Adobe will send a signed copy after completion.
                </p>
              </div>
            </div>
            <Link
              to="/employee/manual"
              className="inline-flex items-center justify-center rounded-md bg-green-700 px-5 py-3 font-semibold text-white transition-colors hover:bg-green-800"
            >
              Review & Sign
            </Link>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Upload Documents</h2>
          <div className="flex items-center justify-center w-full">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-8 h-8 mb-3 text-gray-400" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">PDF, DOC, or DOCX (MAX. 5MB)</p>
              </div>
              <input
                type="file"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
                accept=".pdf,.doc,.docx"
              />
            </label>
          </div>
          {uploading && (
            <div className="mt-4 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div>
              <p className="mt-2 text-sm text-gray-600">Uploading...</p>
            </div>
          )}
        </div>

        {/* Uploaded Documents Section */}
        {uploadedDocs.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-green-800 mb-6">Your Uploaded Documents</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {uploadedDocs.map((doc) => (
                <div key={doc.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-300 border-b-4 border-transparent hover:border-orange-500">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="bg-orange-100 p-2 rounded-full mr-3 inline-block mb-4">
                        <FileText className="h-6 w-6 text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-green-800 mb-2">
                        {doc.fileName}
                      </h3>
                      <p className={`text-sm ${
                        doc.status === 'approved' ? 'text-green-600' : 
                        doc.status === 'rejected' ? 'text-red-600' : 
                        'text-orange-600'
                      }`}>
                        Status: {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Uploaded: {new Date(doc.uploadDate).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => window.open(doc.fileUrl, '_blank')}
                      className="text-orange-500 hover:text-orange-700 transition-colors"
                      title="View Document"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Onboarding Documents Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-green-800 mb-6">Onboarding Documents</h2>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : documents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {documents.map((document) => (
                <div
                  key={document.id}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-300 border-b-4 border-transparent hover:border-orange-500"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="bg-orange-100 p-2 rounded-full mr-3 inline-block mb-4">
                        <FileText className="h-6 w-6 text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-green-800 mb-2">
                        {document.title}
                      </h3>
                      {document.error && (
                        <p className="text-sm text-red-600 mt-2">{document.error}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDownload(document)}
                      disabled={!document.downloadUrl && !document.error}
                      className={`text-orange-500 hover:text-orange-700 transition-colors ${
                        !document.downloadUrl && !document.error ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="Download Document"
                    >
                      <Download className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents Available</h3>
              <p className="text-sm text-gray-500">
                There are no onboarding documents available at the moment.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentsPage;
